import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { storage } from "../utils/storage";
import { AIModel, getModelTier, TIER_DISPLAY, canAccessModel } from "../types/AIModel";
import useAIModels from "../hooks/useAIModels";
import PremiumPopup from "./PremiumPopup";
import { useUserTier } from "../hooks/useUserTier";

const MOBILE_CARD_WIDTH = "168px";
const DESKTOP_CARD_WIDTH = "248px";
const MOBILE_CARD_MIN_HEIGHT = "158px";
const DESKTOP_CARD_MIN_HEIGHT = "132px";

interface CardModelProps {
  onSelectModel?: (model: AIModel) => void;
  listStyle?: boolean;
}

export default function CardModel({
  onSelectModel,
  listStyle,
}: CardModelProps) {
  const [selectedModel, setSelectedModel] = useState<AIModel | null>(null);
  const [showPremiumPopup, setShowPremiumPopup] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const navigate = useNavigate();
  const { categories, models: allModels, loading } = useAIModels();
  const userTier = useUserTier();

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 768);
      setIsTablet(window.innerWidth >= 768 && window.innerWidth < 1024);
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);

    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  useEffect(() => {
    if (allModels.length) {
      if (listStyle && allModels.length > 0) {
        if (
          !selectedModel ||
          !allModels.some((m) => m.id === selectedModel.id)
        ) {
          const firstModel = allModels[0];
          setSelectedModel(firstModel);
        }
        return;
      }
      const savedModelId = localStorage.getItem("selectedModelId");
      if (savedModelId) {
        const savedModel = allModels.find(
          (model) => model.id.toString() === savedModelId
        );
        if (savedModel) {
          setSelectedModel(savedModel);
          // Only auto-select if user has access to this model
          const modelTier = getModelTier(savedModel);
          if (canAccessModel(modelTier, userTier)) {
            const token = storage.getJwtToken();
            if (token) {
              onSelectModel?.(savedModel);
            }
          }
          return;
        }
      }
      if (!selectedModel) {
        const firstModel = allModels[0];
        setSelectedModel(firstModel);
        // Only auto-select if user has access to this model
        const modelTier = getModelTier(firstModel);
        if (canAccessModel(modelTier, userTier)) {
          const token = storage.getJwtToken();
          if (token) {
            onSelectModel?.(firstModel);
          }
        }
      }
    }
  }, [allModels, listStyle, onSelectModel, selectedModel, userTier]);

  useEffect(() => {
    if (listStyle) {
      setSelectedModel(null);
    }
    // eslint-disable-next-line
  }, [listStyle]);

  // Note: Model selection logic is now inline in onClick handlers
  // This allows better access to computed values like modelTier and hasAccess

  const categoryIcons: Record<string, string> = {
    "Chat": "💬",
    "Image Generation": "🎨",
    "Document Analysis": "📄",
    "Audio & Video": "🎬",
    "🔍 Explore": "🔍",
    "📚 Study": "📚",
    "🌿 Lifestyle": "🌿",
    "📱 Social": "🧩",
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <>
      <div className="flex flex-col mt-2">
        {categories.map((category) => (
          <div key={category.id}>
            <div className="flex items-center gap-2 my-4">
              <span className="w-6 h-6">
                {categoryIcons[category.name] || "📁"}
              </span>
              <span className="text-base font-bold">
                {category.name}
              </span>
            </div>
            <div
              className={
                listStyle
                  ? "flex flex-col gap-2"
                  : `flex gap-2 pb-2 ${
                      isMobile || isTablet
                        ? "flex-nowrap flex-row scrollbar-subtle"
                        : "scrollbar-subtle"
                    }`
              }
              style={listStyle ? {} : {
                WebkitOverflowScrolling: "touch",
                overflowX: "auto",
              }}>
              {category.ai_models.map((model: AIModel, idx: number) => {
                const isSelected =
                  listStyle && selectedModel && selectedModel.id === model.id;
                const modelTier = getModelTier(model);
                const hasAccess = canAccessModel(modelTier, userTier);

                return (
                  <div
                    key={model.id}
                    onClick={(e) => {
                      e.stopPropagation();
                      const token = storage.getJwtToken();
                      if (!token) {
                        navigate("/get-started");
                        return;
                      }
                      if (hasAccess) {
                        navigate(`/chat/${model.id}`);
                      } else {
                        setShowPremiumPopup(true);
                      }
                    }}
                    style={
                      listStyle
                        ? {
                            background: "#212226",
                            borderRadius: 18,
                            cursor: "pointer",
                            padding: "12px 16px",
                            border: isSelected
                              ? "2px solid #3AF062"
                              : "2px solid transparent",
                            width: "100%",
                            boxSizing: "border-box",
                          }
                        : {
                            background: "rgba(0, 0, 0, 0.30)",
                            borderRadius: 18,
                            cursor: "pointer",
                            padding: "12px 16px",
                            transition: "box-shadow 0.2s, border 0.2s",
                            border: isSelected
                              ? "2px solid #3AF062"
                              : "2px solid transparent",
                            width: isMobile
                              ? MOBILE_CARD_WIDTH
                              : DESKTOP_CARD_WIDTH,
                            minWidth: isMobile
                              ? MOBILE_CARD_WIDTH
                              : DESKTOP_CARD_WIDTH,
                            maxWidth: isMobile
                              ? MOBILE_CARD_WIDTH
                              : DESKTOP_CARD_WIDTH,
                            minHeight:
                              isMobile || isTablet
                                ? MOBILE_CARD_MIN_HEIGHT
                                : DESKTOP_CARD_MIN_HEIGHT,
                            boxSizing: "border-box",
                            flexShrink: 0,
                          }
                    }
                    className={
                      listStyle
                        ? "flex flex-col justify-start w-full relative"
                        : `flex flex-col justify-start flex-shrink-0 ${
                            isMobile || isTablet ? "flex-grow" : ""
                          }`
                    }>
                    <div className="flex items-center justify-between mb-3">
                      {/* Tier Badge */}
                      <div
                        style={{
                          background: TIER_DISPLAY[modelTier].bgColor,
                          color: TIER_DISPLAY[modelTier].color,
                          fontSize: 8,
                          letterSpacing: "1.2px",
                          cursor: !hasAccess ? "pointer" : "default",
                          padding: "2px 8px",
                          borderRadius: 100,
                          fontWeight: 500,
                        }}
                        onClick={
                          !hasAccess
                            ? (e) => {
                                e.stopPropagation();
                                setShowPremiumPopup(true);
                              }
                            : undefined
                        }
                        title={!hasAccess ? "Click to view premium plans" : ""}>
                        {TIER_DISPLAY[modelTier].label}
                      </div>
                      {listStyle ? (
                        isSelected && (
                          <img
                            src="/assets/images/icon-select-ok.svg"
                            alt="selected"
                            className="w-6 h-6"
                          />
                        )
                      ) : (
                        <img
                          src="/assets/images/icon-arrow-small.svg"
                          alt="status-icon"
                          className="w-6 h-6"
                          onClick={(e) => {
                            e.stopPropagation();
                            const token = storage.getJwtToken();
                            if (!token) {
                              navigate("/get-started");
                              return;
                            }
                            if (hasAccess) {
                              navigate(`/chat/${model.id}`);
                            } else {
                              setShowPremiumPopup(true);
                            }
                          }}
                        />
                      )}
                    </div>
                    <div className="flex flex-col">
                      <h4
                        className="font-bold mb-1 text-sm"
                        style={{ color: "#fff" }}>
                        {model.title}
                      </h4>
                      <p style={{ fontSize: 12, color: "#B0B8D1" }}>
                        {model.short_description}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      <PremiumPopup
        isOpen={showPremiumPopup}
        onClose={() => setShowPremiumPopup(false)}
      />
    </>
  );
}
